import {
  railCardWidth,
  type StorefrontSizeToken,
} from "@/domain/storefront-appearance";
import { useCallback, useRef, useState } from "react";

/**
 * From here the rail FILLS the column; below, it SCROLLS one card per view.
 * Tailwind `md` — same step where the storefront switches to two columns.
 */
const RAIL_FILL_QUERY = "(min-width: 768px)";

/** Element CONTENT width: the FIT, already without both gutters. */
function contentWidth(element: HTMLElement): number {
  const { paddingLeft, paddingRight } = getComputedStyle(element);
  return (
    element.clientWidth -
    (parseFloat(paddingLeft) || 0) -
    (parseFloat(paddingRight) || 0)
  );
}

function rootFontSize(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

/**
 * Measures the rail and returns each card's width. Measured (not CSS) because
 * redistribution needs a reliable `floor` of a ratio of real width. Measures
 * via REF callback so the card is born at the right width. Returns `null`
 * until the first measure.
 */
interface RailCardWidth {
  size: StorefrontSizeToken;
  shrinkRem: number;
}

export function useRailCardWidth({
  size,
  shrinkRem,
}: RailCardWidth): {
  /**
   * CALLBACK ref, not object ref — measure fires the instant the rail enters
   * the DOM (rails mount late: reorder after orders, item rails after catalog).
   * Object ref + useLayoutEffect ran once while still returning null and never
   * reattached.
   */
  railRef: (node: HTMLDivElement | null) => void;
  cardWidth: number | null;
} {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [cardWidth, setCardWidth] = useState<number | null>(null);

  const measure = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    const root = rootFontSize();
    const fitWidth = contentWidth(element);
    if (fitWidth <= 0) return;

    const fills = window.matchMedia(RAIL_FILL_QUERY).matches;

    setCardWidth(
      railCardWidth({
        device: fills ? "desktop" : "mobile",
        size,
        fitWidth,
        shrink: shrinkRem * root,
        rootFontSize: root,
      }),
    );
  }, [size, shrinkRem]);

  const railRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      elementRef.current = node;

      if (!node) return;

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
    },
    [measure],
  );

  return { railRef, cardWidth };
}
